import axios from 'axios';

// Cache untuk data admin (refresh setiap 5 menit)
let adminAccessCache: {
  data: { adminEmails: string[]; superadminEmails: string[] } | null;
  timestamp: number;
  isFetching: boolean;
} = {
  data: null,
  timestamp: 0,
  isFetching: false
};

const CACHE_TTL = 5 * 60 * 1000; // 5 menit

// Fallback data jika API tidak tersedia (kosong karena data diambil dari spreadsheet)
// Jika API error, tidak ada email yang akan dianggap sebagai admin/superadmin
const FALLBACK_ADMIN_EMAILS: string[] = [];
const FALLBACK_SUPERADMIN_EMAILS: string[] = [];

// Fungsi untuk mengambil data admin dari API
export async function fetchAdminAccessData() {
  try {
    const response = await axios.get('/api/bookings?action=admin-access');
    return response.data;
  } catch (error) {
    console.error('Error fetching admin access data:', error);
    // Return fallback data jika error
    return {
      adminEmails: FALLBACK_ADMIN_EMAILS,
      superadminEmails: FALLBACK_SUPERADMIN_EMAILS
    };
  }
}

// Fungsi untuk refresh cache di background
function refreshCacheInBackground() {
  if (adminAccessCache.isFetching) return;
  
  adminAccessCache.isFetching = true;
  fetchAdminAccessData()
    .then(data => {
      adminAccessCache.data = data;
      adminAccessCache.timestamp = Date.now();
      adminAccessCache.isFetching = false;
    })
    .catch(() => {
      adminAccessCache.isFetching = false;
    });
}

// Fungsi untuk mendapatkan data admin (dengan cache)
function getAdminAccessDataSync() {
  const now = Date.now();
  
  // Jika cache masih valid, gunakan cache
  if (adminAccessCache.data && (now - adminAccessCache.timestamp) < CACHE_TTL) {
    return adminAccessCache.data;
  }
  
  // Jika cache expired atau belum ada, refresh di background
  if (!adminAccessCache.isFetching) {
    refreshCacheInBackground();
  }
  
  // Return fallback data sementara jika cache belum ada
  if (!adminAccessCache.data) {
    return {
      adminEmails: FALLBACK_ADMIN_EMAILS,
      superadminEmails: FALLBACK_SUPERADMIN_EMAILS
    };
  }
  
  // Return cache yang ada (meskipun expired, lebih baik daripada fallback)
  return adminAccessCache.data;
}

// Inisialisasi: fetch data saat pertama kali module di-load
if (typeof window !== 'undefined') {
  refreshCacheInBackground();
}

// Fungsi helper untuk mengecek apakah email adalah superadmin
export const isSuperAdminEmail = (email: string): boolean => {
  if (!email) return false;
  const normalizedEmail = email.trim().toLowerCase(); // Normalisasi email ke lowercase
  const data = getAdminAccessDataSync();
  return data.superadminEmails.includes(normalizedEmail);
};

// Fungsi helper untuk mengecek apakah email adalah admin (biasa)
export const isAdminEmail = (email: string): boolean => {
  if (!email) return false;
  const normalizedEmail = email.trim().toLowerCase(); // Normalisasi email ke lowercase
  const data = getAdminAccessDataSync();
  return data.adminEmails.includes(normalizedEmail);
};

// Fungsi helper untuk mengecek apakah email adalah admin atau superadmin
export const isAdminOrSuperAdmin = (email: string): boolean => {
  if (!email) return false;
  const normalizedEmail = email.trim().toLowerCase(); // Normalisasi email ke lowercase
  const data = getAdminAccessDataSync();
  const isSuperAdmin = data.superadminEmails.includes(normalizedEmail);
  const isAdmin = data.adminEmails.includes(normalizedEmail);
  const result = isSuperAdmin || isAdmin;
  
  // Debug log untuk troubleshooting
  if (typeof window !== 'undefined' && window.location.pathname.includes('update-score')) {
    console.log('isAdminOrSuperAdmin check:', {
      email,
      normalizedEmail,
      isSuperAdmin,
      isAdmin,
      result,
      superadminEmails: data.superadminEmails,
      adminEmails: data.adminEmails,
      cacheTimestamp: adminAccessCache.timestamp,
      cacheAge: Date.now() - adminAccessCache.timestamp
    });
  }
  
  return result;
};

// Export untuk backward compatibility (akan deprecated)
// Array kosong karena data sekarang diambil dari spreadsheet
export const SUPERADMIN_EMAILS: string[] = [];
export const ADMIN_EMAILS: string[] = [];
